import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';

import { PackCardItem } from '@/components/app/cards/PacksList/types';
import { FORTY_PERCENT_OFF_COUPON } from '@/components/app/layout/DiscountModal';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { CANCELLATION_FLOW_EXPERIMENT } from '@/components/app/manageSubscription/CancellationFlow/util';
import { getPauseState } from '@/components/manageSubscription/Pause/util';
import { useUserState } from '@/hooks/store';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';

export const useLockedContentRouting = (): ((packItem: PackCardItem) => Promise<void>) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const user = useUserState();
	const { setCurrentPackItem } = usePacksContext();
	const { isEnrolled } = useAmplitudeExperiment(
		'www-locked-content-upsell',
		searchParams?.get('utm_medium') === 'paid',
	);

	// The resume-pause screen on /manage-subscription only renders when the
	// cancellation-flow experiment is enrolled, otherwise an active-paused
	// (`valid: false`) sub is shown the "expired" state. `shortCircuit` skips evaluation
	// (and its exposure) unless the user is actually active-paused, a rare state.
	const isActivePaused =
		user?.subscription?.type === 'stripe' && getPauseState(user?.subscription) === 'active';
	const { isEnrolled: isCancelEnrolled } = useAmplitudeExperiment(
		CANCELLATION_FLOW_EXPERIMENT,
		!isActivePaused,
	);
	const canResumeInPlace = isActivePaused && Boolean(isCancelEnrolled);

	return async (packItem: PackCardItem) => {
		// An actively-paused subscriber reads as `valid: false`, so they would otherwise be
		// shown the new-member upsell and stack a second subscription on the paused one.
		if (canResumeInPlace) {
			await router.push('/app/profile/manage-subscription?resume=1');
			return;
		}

		if (isEnrolled) {
			setCurrentPackItem(packItem);
		} else {
			await router.push(`/new-member-offer?coupon=${FORTY_PERCENT_OFF_COUPON}` as Route);
		}
	};
};
